/**
 * chartResolution.js - TradingView Chart Resolution Detection Utility
 *
 * WHY THIS EXISTS:
 * ================
 * Backtests can run at different time cadences (1-minute, 5-minute, daily, etc.).
 * The TradingView chart needs to know the correct resolution to:
 * 1. Display data at the appropriate granularity
 * 2. Not collapse intraday timestamps to midnight (which destroys minute-level data)
 * 3. Enable proper zooming and time navigation
 *
 * PROBLEM SOLVED:
 * ===============
 * Previously, the chart was hardcoded to daily ('D') resolution. This caused
 * one-minute backtest data to be collapsed to daily bars, losing all intraday
 * detail. This utility auto-detects the resolution from the data itself.
 *
 * HOW IT WORKS:
 * =============
 * 1. Analyzes the time gaps between consecutive data points
 * 2. Calculates the median gap (more robust than average against outliers)
 * 3. Maps the gap to the closest TradingView resolution
 *
 * TRADINGVIEW RESOLUTION FORMAT:
 * ==============================
 * - '1'   = 1 minute
 * - '5'   = 5 minutes
 * - '15'  = 15 minutes
 * - '30'  = 30 minutes
 * - '60'  = 1 hour
 * - '240' = 4 hours
 * - 'D'   = 1 day
 * - 'W'   = 1 week
 *
 * @module chartResolution
 */

/**
 * Resolution definitions mapping time gaps (in seconds) to TradingView resolutions.
 * Each resolution has:
 * - name: TradingView resolution string
 * - seconds: Expected gap between bars in seconds
 * - maxGap: Maximum gap to still consider this resolution (halfway to next)
 * - isIntraday: Whether this is an intraday resolution (affects timestamp handling)
 *
 * @constant {Array<Object>}
 */
export const RESOLUTION_DEFINITIONS = [
  { name: '1',   seconds: 60,      maxGap: 150,     isIntraday: true,  label: '1 minute' },
  { name: '5',   seconds: 300,     maxGap: 600,     isIntraday: true,  label: '5 minutes' },
  { name: '15',  seconds: 900,     maxGap: 1350,    isIntraday: true,  label: '15 minutes' },
  { name: '30',  seconds: 1800,    maxGap: 2700,    isIntraday: true,  label: '30 minutes' },
  { name: '60',  seconds: 3600,    maxGap: 7200,    isIntraday: true,  label: '1 hour' },
  { name: '240', seconds: 14400,   maxGap: 43200,   isIntraday: true,  label: '4 hours' },
  { name: 'D',   seconds: 86400,   maxGap: 259200,  isIntraday: false, label: '1 day' },
  { name: 'W',   seconds: 604800,  maxGap: Infinity, isIntraday: false, label: '1 week' },
];

/**
 * Detects the chart resolution from indicator data points by analyzing timestamp gaps.
 *
 * ALGORITHM:
 * 1. Extract timestamps from data points
 * 2. Calculate gaps between consecutive timestamps
 * 3. Find the median gap (robust against outliers like weekend gaps)
 * 4. Match the median gap to the closest resolution definition
 *
 * WHY MEDIAN (not average):
 * - Stock market data has weekend gaps (2+ days between Friday close and Monday open)
 * - Using average would skew toward longer resolutions
 * - Median ignores these outliers and finds the "typical" gap
 *
 * @param {Array<Object>} points - Array of data points with 'time' property
 *                                 Time can be in seconds (Unix) or milliseconds
 * @param {Object} options - Optional configuration
 * @param {number} options.sampleSize - Max points to analyze (default: 100, for performance)
 * @param {string} options.defaultResolution - Fallback if detection fails (default: 'D')
 *
 * @returns {Object} Resolution info:
 *   - resolution: TradingView resolution string ('1', '5', '15', '60', '240', 'D', 'W')
 *   - isIntraday: Boolean indicating if this is intraday data
 *   - label: Human-readable label ('1 minute', '5 minutes', etc.)
 *   - medianGapSeconds: The detected median gap between points
 *   - confidence: 'high', 'medium', or 'low' based on data consistency
 *
 * @example
 * // One-minute data
 * const minuteData = [
 *   { time: 1704067200, value: 100 }, // 2024-01-01 00:00:00
 *   { time: 1704067260, value: 101 }, // 2024-01-01 00:01:00
 *   { time: 1704067320, value: 102 }, // 2024-01-01 00:02:00
 * ];
 * detectResolutionFromData(minuteData);
 * // Returns: { resolution: '1', isIntraday: true, label: '1 minute', ... }
 *
 * @example
 * // Daily data
 * const dailyData = [
 *   { time: 1704067200, value: 100 }, // 2024-01-01
 *   { time: 1704153600, value: 101 }, // 2024-01-02
 *   { time: 1704240000, value: 102 }, // 2024-01-03
 * ];
 * detectResolutionFromData(dailyData);
 * // Returns: { resolution: 'D', isIntraday: false, label: '1 day', ... }
 */
export function detectResolutionFromData(points, options = {}) {
  const {
    sampleSize = 100,
    defaultResolution = 'D'
  } = options;

  // Handle edge cases: no data or single point
  if (!points || !Array.isArray(points) || points.length < 2) {
    console.log('[detectResolution] Insufficient data points, using default:', defaultResolution);
    return getResolutionInfo(defaultResolution, null, 'low');
  }

  // Extract and normalize timestamps to seconds
  // Points may have time in seconds (Unix) or milliseconds
  const timestamps = points
    .slice(0, sampleSize) // Limit for performance on large datasets
    .map(p => {
      if (typeof p.time === 'number') {
        // If time > 10 billion, it's in milliseconds - convert to seconds
        return p.time > 10000000000 ? Math.floor(p.time / 1000) : p.time;
      }
      // Handle string dates
      return Math.floor(new Date(p.time).getTime() / 1000);
    })
    .filter(t => !isNaN(t) && isFinite(t))
    .sort((a, b) => a - b); // Ensure ascending order

  if (timestamps.length < 2) {
    console.log('[detectResolution] No valid timestamps after filtering, using default:', defaultResolution);
    return getResolutionInfo(defaultResolution, null, 'low');
  }

  // Calculate gaps between consecutive timestamps
  const gaps = [];
  for (let i = 1; i < timestamps.length; i++) {
    const gap = timestamps[i] - timestamps[i - 1];
    // Only include positive gaps (data should be ascending)
    if (gap > 0) {
      gaps.push(gap);
    }
  }

  if (gaps.length === 0) {
    console.log('[detectResolution] No valid gaps found, using default:', defaultResolution);
    return getResolutionInfo(defaultResolution, null, 'low');
  }

  // Calculate median gap (robust against outliers like weekend gaps)
  const medianGap = calculateMedian(gaps);

  // Find the matching resolution
  const matchedResolution = findMatchingResolution(medianGap);

  // Calculate confidence based on gap consistency
  const confidence = calculateConfidence(gaps, matchedResolution.seconds);

  console.log('[detectResolution] Detected resolution:', {
    resolution: matchedResolution.name,
    medianGapSeconds: medianGap,
    expectedGapSeconds: matchedResolution.seconds,
    isIntraday: matchedResolution.isIntraday,
    confidence,
    sampleSize: timestamps.length,
    gapCount: gaps.length
  });

  return {
    resolution: matchedResolution.name,
    isIntraday: matchedResolution.isIntraday,
    label: matchedResolution.label,
    medianGapSeconds: medianGap,
    expectedGapSeconds: matchedResolution.seconds,
    confidence
  };
}

/**
 * Calculates the median of an array of numbers.
 *
 * @param {number[]} arr - Array of numbers
 * @returns {number} The median value
 *
 * @example
 * calculateMedian([1, 2, 3, 4, 5]); // Returns 3
 * calculateMedian([1, 2, 3, 4]); // Returns 2.5
 */
export function calculateMedian(arr) {
  if (!arr || arr.length === 0) return 0;

  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

/**
 * Finds the resolution definition that best matches a given gap in seconds.
 *
 * @param {number} gapSeconds - The gap between data points in seconds
 * @returns {Object} The matching resolution definition
 */
export function findMatchingResolution(gapSeconds) {
  // Find the first resolution where the gap is less than maxGap
  for (const resolution of RESOLUTION_DEFINITIONS) {
    if (gapSeconds <= resolution.maxGap) {
      return resolution;
    }
  }

  // Default to weekly if gap is very large
  return RESOLUTION_DEFINITIONS[RESOLUTION_DEFINITIONS.length - 1];
}

/**
 * Calculates confidence level based on how consistent the gaps are.
 *
 * @param {number[]} gaps - Array of gap values in seconds
 * @param {number} expectedGap - The expected gap for the detected resolution
 * @returns {string} 'high', 'medium', or 'low'
 */
export function calculateConfidence(gaps, expectedGap) {
  if (!gaps || gaps.length === 0) return 'low';

  // Calculate what percentage of gaps are within 50% of expected
  const tolerance = expectedGap * 0.5;
  const matchingGaps = gaps.filter(g =>
    Math.abs(g - expectedGap) <= tolerance ||
    // Also allow gaps that are close to multiples (accounts for weekends, holidays)
    Math.abs(g - expectedGap * 2) <= tolerance ||
    Math.abs(g - expectedGap * 3) <= tolerance
  );

  const matchRatio = matchingGaps.length / gaps.length;

  if (matchRatio >= 0.8) return 'high';
  if (matchRatio >= 0.5) return 'medium';
  return 'low';
}

/**
 * Helper function to get resolution info by name.
 *
 * @param {string} resolutionName - TradingView resolution string
 * @param {number|null} medianGap - The detected median gap (or null)
 * @param {string} confidence - Confidence level
 * @returns {Object} Resolution info object
 */
function getResolutionInfo(resolutionName, medianGap, confidence) {
  const resolution = RESOLUTION_DEFINITIONS.find(r => r.name === resolutionName) ||
    RESOLUTION_DEFINITIONS.find(r => r.name === 'D'); // Default to daily

  return {
    resolution: resolution.name,
    isIntraday: resolution.isIntraday,
    label: resolution.label,
    medianGapSeconds: medianGap,
    expectedGapSeconds: resolution.seconds,
    confidence
  };
}

/**
 * Maps a Lumibot sleeptime string to TradingView resolution.
 * Lumibot uses strings like "1M", "5M", "1H", "1D" for sleeptime.
 *
 * This is useful if cadence info is available from the backend
 * instead of auto-detecting from data.
 *
 * @param {string} sleeptime - Lumibot sleeptime string (e.g., "1M", "5M", "1H", "1D")
 * @returns {Object} Resolution info object
 *
 * @example
 * mapSleeptimeToResolution("1M"); // Returns { resolution: '1', isIntraday: true, ... }
 * mapSleeptimeToResolution("1D"); // Returns { resolution: 'D', isIntraday: false, ... }
 */
export function mapSleeptimeToResolution(sleeptime) {
  if (!sleeptime || typeof sleeptime !== 'string') {
    return getResolutionInfo('D', null, 'low');
  }

  const normalized = sleeptime.toUpperCase().trim();

  // Parse the sleeptime string
  const match = normalized.match(/^(\d+)([MHDSW])$/);
  if (!match) {
    console.warn('[mapSleeptimeToResolution] Invalid sleeptime format:', sleeptime);
    return getResolutionInfo('D', null, 'low');
  }

  const value = parseInt(match[1], 10);
  const unit = match[2];

  // Map to TradingView resolution
  let resolution;
  switch (unit) {
    case 'M': // Minutes
      if (value === 1) resolution = '1';
      else if (value === 5) resolution = '5';
      else if (value === 15) resolution = '15';
      else if (value === 30) resolution = '30';
      else if (value === 60) resolution = '60';
      else resolution = String(value); // Custom minute resolution
      break;
    case 'H': // Hours
      if (value === 1) resolution = '60';
      else if (value === 4) resolution = '240';
      else resolution = String(value * 60); // Convert to minutes
      break;
    case 'D': // Days
      resolution = 'D';
      break;
    case 'W': // Weeks
      resolution = 'W';
      break;
    default:
      resolution = 'D';
  }

  const resolutionDef = RESOLUTION_DEFINITIONS.find(r => r.name === resolution) ||
    RESOLUTION_DEFINITIONS.find(r => r.name === 'D');

  return {
    resolution: resolutionDef.name,
    isIntraday: resolutionDef.isIntraday,
    label: resolutionDef.label,
    medianGapSeconds: resolutionDef.seconds,
    expectedGapSeconds: resolutionDef.seconds,
    confidence: 'high' // High confidence because it's explicit
  };
}

// Default export for convenience
export default {
  detectResolutionFromData,
  mapSleeptimeToResolution,
  calculateMedian,
  findMatchingResolution,
  calculateConfidence,
  RESOLUTION_DEFINITIONS
};
